/**
 * Call-out coverage (4.1): ranked candidates in two pools. Working today — route
 * slack, drive-time delta, competency and continuity match, ceiling impact in
 * points. Not working — ranked by the cost of the ask: overflow shift today (no
 * ask), per-diem who declared availability, per-diem who have not, staff on a day
 * off; order configurable per branch. No fairness or rotation-load logic.
 *
 * "Working today" means a route today, not merely availability: a per diem who
 * declared the day and has not been booked is still an ask, so he reads under
 * **External, availability declared** rather than beside the clinicians already
 * out on the road.
 *
 * The competency check and the drive estimate are the ones `assignment.ts` uses
 * for the contested row, so the coverage pools and Act 2's options cannot
 * disagree about who covers a territory or by how much further they would drive.
 */
import type { DateKey, DemoState, PoolKind, RosterEntry, VisitResourceLike } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addDaysToKey, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { clinicianDayFacts, shiftsOn } from "./clinicianDay";
import { effectiveVisitsOnDay } from "./effectiveCalendar";
import { hasTraits, primaryTerritory, rosterEntries } from "./roster";
import { estimateDriveMinutes, territoryForZip } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";

export { estimateDriveMinutes };
import { roundPoints, selectWeekPoints } from "./utilization";
import { pointsFor, visitTypeKindFor } from "@caredna-marlin/utils/homeHealthDemo/engine/visitTypes";

export interface CallOutInput {
  providerHandle: string;
  date: DateKey;
  /** Only visits starting at/after this instant need cover. */
  fromIso: string;
  requiredTraits?: string[];
  /** Narrow the ranking to one of the unfilled visits (the drawer's selected row). */
  visitIds?: number[];
}

export interface Ranked {
  handle: string;
  displayName: string;
  discipline: string;
  employment: RosterEntry["employment"];
  score: number;
  reasons: string[];
  exclusions: string[];
  /** Working pool metrics */
  slackMinutes?: number;
  neededMinutes?: number;
  driveDeltaMinutes?: number;
  continuityVisits?: number;
  traitMatch: boolean;
  /** Points, never hours: the planning unit every other screen shows. */
  ceilingImpact?: { pointsAfter: number; ceilingPoints: number; overCeiling: boolean };
  /** Not-working pool classification */
  pool?: PoolKind;
}

export interface CallOutRanking {
  visits: VisitResourceLike[];
  requiredTraits: string[];
  working: Ranked[];
  notWorking: Ranked[];
  poolOrder: PoolKind[];
}

export const POOL_LABELS: Record<PoolKind, string> = {
  overflow: "On an overflow shift today — no ask",
  perDiemDeclared: "External, availability declared",
  perDiemUndeclared: "External, availability not declared",
  dayOff: "Staff on a day off",
};

function disciplinesCover(state: DemoState, entry: RosterEntry, visits: VisitResourceLike[]): boolean {
  return visits.every((vr) => {
    const kind = visitTypeKindFor(state.config, state.meta.bindings, vr.visitTypeId, vr.visitTypeName);
    if (!kind) return entry.discipline === "RN" || entry.discipline === "LPN";
    return state.config.visitTypes[kind].disciplines.includes(entry.discipline);
  });
}

function continuityCount(state: DemoState, providerId: number | undefined, visits: VisitResourceLike[]): number {
  if (providerId === undefined) return 0;
  const patientIds = new Set(visits.map((v) => v.patientId));
  const seen = new Set<number>();
  for (const vr of state.server.visitResources) {
    if (vr.providerId === providerId && vr.eventStatus === "complete" && patientIds.has(vr.patientId)) seen.add(vr.patientId);
  }
  return seen.size;
}

export function selectCalloutCandidates(state: DemoState, input: CallOutInput): CallOutRanking {
  const tz = state.config.branch.timeZone;
  const calledOutId = state.meta.bindings.providerIdsByHandle[input.providerHandle];
  const onDay = effectiveVisitsOnDay(state, input.date, tz);
  const visits = onDay
    .filter((vr) => vr.providerId === calledOutId && new Date(vr.startIso) >= new Date(input.fromIso))
    .filter((vr) => !input.visitIds || input.visitIds.includes(vr.visitId));
  const requiredTraits = input.requiredTraits ?? [];
  const neededMinutes = visits.reduce((s, vr) => s + vr.durationMinutes + vr.interVisitDriveMinutes, 0);
  const addedPoints = roundPoints(visits.reduce((s, vr) => s + pointsFor(state.config, visitTypeKindFor(state.config, state.meta.bindings, vr.visitTypeId, vr.visitTypeName)), 0));
  const working: Ranked[] = [];
  const notWorking: Ranked[] = [];
  const weekStart = weekStartKey(input.date);

  for (const entry of rosterEntries(state)) {
    if (entry.handle === input.providerHandle) continue;
    const providerId = state.meta.bindings.providerIdsByHandle[entry.handle] ?? entry.providerId;
    const facts = clinicianDayFacts(state, entry, input.date);
    const traitMatch = hasTraits(entry, requiredTraits);
    const exclusions: string[] = [];
    if (!traitMatch) exclusions.push(`Missing ${requiredTraits.filter((t) => !entry.traits.includes(t)).join(", ")}`);
    if (!disciplinesCover(state, entry, visits)) exclusions.push("Discipline cannot take these visit types");
    const cont = continuityCount(state, providerId, visits);
    // A route today, not merely a declared day: a per diem nobody has booked is
    // still an ask, and belongs with the others whose cost of the ask is ranked.
    const onTheRoad = onDay.some((vr) => vr.providerId === providerId);

    if (facts.hasShift && !facts.overflowMinutes && (entry.employment !== "perDiem" || onTheRoad)) {
      const slack = Math.max(0, facts.shiftMinutes - facts.blockMinutes - facts.bookedMinutes - facts.driveMinutes);
      const from = primaryTerritory(entry, state.config.branch);
      const driveDelta = visits.reduce((s, vr) => s + estimateDriveMinutes(from, territoryForZip(state.config.branch, vr.patientZip)), 0);
      const week = selectWeekPoints(state, entry.handle, weekStart);
      const pointsAfter = roundPoints(week.firm + addedPoints);
      const overCeiling = pointsAfter > week.ceiling;
      const fits = slack >= neededMinutes + driveDelta;
      const reasons = [
        fits ? `Route slack ${Math.round(slack / 60 * 10) / 10}h covers ${Math.round((neededMinutes + driveDelta) / 60 * 10) / 10}h` : `Only ${Math.round(slack / 60 * 10) / 10}h slack for ${Math.round((neededMinutes + driveDelta) / 60 * 10) / 10}h`,
        `+${driveDelta} min drive (estimate from ${from})`,
        cont ? `Has seen ${cont} of these patient${cont === 1 ? "" : "s"}` : "No continuity with these patients",
        overCeiling ? `Would pass the ceiling — ${pointsAfter} of ${week.ceiling} points after` : `Week at ${pointsAfter} of ${week.ceiling} points after`,
      ];
      const score =
        (fits ? 40 : Math.max(0, 40 * (slack / Math.max(neededMinutes + driveDelta, 1)))) +
        Math.max(0, 25 - driveDelta / 4) +
        (cont ? 15 : 0) +
        (traitMatch ? 10 : 0) +
        (overCeiling ? 0 : 10);
      working.push({ handle: entry.handle, displayName: entry.displayName, discipline: entry.discipline, employment: entry.employment, score: Math.round(score), reasons, exclusions, slackMinutes: slack, neededMinutes: neededMinutes + driveDelta, driveDeltaMinutes: driveDelta, continuityVisits: cont, traitMatch, ceilingImpact: { pointsAfter, ceilingPoints: week.ceiling, overCeiling } });
      continue;
    }

    let pool: PoolKind;
    if (facts.hasShift && facts.overflowMinutes > 0) pool = "overflow";
    else if (entry.employment === "perDiem") {
      // Day 0 counts: the per diem who declared today and was never booked is the
      // first ask, not the last.
      const declaredNearby = [0, -7, -6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6, 7].some((d) => shiftsOn(state.server.shifts, providerId, addDaysToKey(input.date, d), tz).length > 0);
      pool = declaredNearby ? "perDiemDeclared" : "perDiemUndeclared";
    } else pool = "dayOff";
    const poolRank = state.config.candidatePoolOrder.indexOf(pool);
    const reasons = [POOL_LABELS[pool], cont ? `Has seen ${cont} of these patient${cont === 1 ? "" : "s"}` : "No continuity with these patients"];
    const score = Math.max(0, 40 - poolRank * 10) + (cont ? 15 : 0) + (traitMatch ? 10 : 0);
    notWorking.push({ handle: entry.handle, displayName: entry.displayName, discipline: entry.discipline, employment: entry.employment, score, reasons, exclusions, continuityVisits: cont, traitMatch, pool });
  }

  const eligible = (r: Ranked) => r.exclusions.length === 0;
  const rank = (a: Ranked, b: Ranked) => Number(eligible(b)) - Number(eligible(a)) || b.score - a.score || a.displayName.localeCompare(b.displayName);
  const poolOrder = state.config.candidatePoolOrder;
  notWorking.sort((a, b) => Number(eligible(b)) - Number(eligible(a)) || poolOrder.indexOf(a.pool!) - poolOrder.indexOf(b.pool!) || b.score - a.score);
  working.sort(rank);
  return { visits, requiredTraits, working, notWorking, poolOrder };
}

